// Small line icons and the round icon button they sit in. Every close / cancel / remove "×" in the app is a
// CloseButton, so they all look and behave alike: a crisp drawn glyph (not a font character, which renders at a
// different weight and baseline on every platform) inside a round hit target that grows to 44 px on touch screens.
import type { ButtonHTMLAttributes } from "react";
import "./icons.css";

/** A crossed-out bell: a muted channel, DM or chat in the lists. Named "Muted" for screen readers. */
export function MutedIcon({ size = 14, className = "row-muted" }: { size?: number; className?: string }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" role="img" aria-label="Muted">
      <path d="M13.7 21a2 2 0 0 1-3.4 0M18.6 14.6c.9-1 1.4-2.2 1.4-3.6a8 8 0 0 0-8-8c-.9 0-1.7.1-2.5.4M6.3 6.3A8 8 0 0 0 4 11c0 5-2 6-2 7h13M2 2l20 20" />
    </svg>
  );
}

/** An × drawn with rounded caps. `size` is the drawn box; the strokes sit inside it with optical padding. */
export function CloseIcon({ size = 14 }: { size?: number }) {
  return (
    <svg className="glyph" width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

/** An "i" in a circle: the viewer's photo info (#379). */
export function InfoIcon({ size = 16 }: { size?: number }) {
  return (
    <svg className="glyph" width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
      <circle cx="8" cy="8" r="6.25" />
      <path d="M8 7.2v3.6" />
      <circle cx="8" cy="5.1" r=".5" fill="currentColor" />
    </svg>
  );
}

/** A chevron pointing down; rotate it with CSS for other directions. */
export function ChevronIcon({ size = 14 }: { size?: number }) {
  return (
    <svg className="glyph" width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

type RoundProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> & {
  /** Required: the button has no visible text. */
  "aria-label": string;
  /** "sm": 24 px target (inline: chips, cards); "md": 28 px (panel and dialog headers). */
  size?: "sm" | "md";
  /** "media": sits over a picture, so it gets its own dark backing instead of the surface's colours. */
  tone?: "plain" | "media";
};

/** The round button every small icon control uses (close, the preview chevron…). */
export function RoundButton({ size = "md", tone = "plain", className, type = "button", children, ...rest }: RoundProps) {
  return (
    <button type={type} className={`round-btn ${size}${tone === "media" ? " on-media" : ""}${className ? ` ${className}` : ""}`} {...rest}>
      {children}
    </button>
  );
}

/** A close / cancel / remove button: CloseIcon in a RoundButton. */
export function CloseButton({ size = "md", ...rest }: Omit<RoundProps, "children">) {
  return (
    <RoundButton size={size} {...rest}>
      <CloseIcon size={size === "sm" ? 14 : 16} />
    </RoundButton>
  );
}
